/* Trust — confianza institucional.
   Decisión deliberada: contraste con el resto del sitio.
   - Fondo blanco (no sand-50) → ruptura visual, sensación de "marco oficial".
   - Movimiento mínimo: solo fade-in del grupo + stroke-drawing en los íconos.
   - Hover: transición de color en el título, sin transforms.
   Cuando el ojo viene de varias secciones con animación, llegar acá quieto
   genera atención por diferencia. */

.section {
  background: var(--sand-100);
  padding-block: var(--space-20);
}

@media (min-width: 768px) {
  .section {
    padding-block: var(--space-24);
  }
}

@media (min-width: 1024px) {
  .section {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

/* ─── Grid de pilares ─── */

.pillars {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-10);
  max-width: 960px;
  margin-inline: auto;

  /* Único movimiento del grupo: fade-in. Sin slide-up para no inducir
     dinamismo — la sobriedad es el punto. */
  opacity: 0;
  transition: opacity 700ms var(--ease-decelerate);
  will-change: opacity;
}

.revealed.pillars {
  opacity: 1;
}

@media (min-width: 768px) {
  .pillars {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-8);
    margin-top: var(--space-4);
  }
}

@media (min-width: 1024px) {
  .pillars {
    gap: var(--space-12);
  }
}

/* ─── Pilar ─── */

.pillar {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-3);
  padding-inline: var(--space-2);
}

@media (min-width: 768px) {
  .pillar {
    padding-inline: var(--space-4);
  }
}

/* Separadores verticales entre pilares en desktop — refuerzan la lectura
   como "tres compromisos discretos" sin recurrir a cards. */
@media (min-width: 768px) {
  .pillar + .pillar {
    border-left: 1px solid var(--color-border-subtle);
  }
}

/* ─── Ícono ─── */

.iconWrap {
  width: 72px;
  height: 72px;
  border-radius: var(--radius-full);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--blue-700);
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
  margin-bottom: var(--space-2);
  /* Sin transform en hover. La sección es "estática por contraste". */
}

/* Stroke-drawing — paths inician invisibles (dashoffset = dasharray) y
   se "dibujan" al revelar la sección. Stagger por pilar via :nth-child. */
.drawPath {
  stroke-dasharray: 200;
  stroke-dashoffset: 200;
  transition: stroke-dashoffset 900ms var(--ease-decelerate);
}

.revealed .pillar:nth-child(1) .drawPath {
  transition-delay: 200ms;
}
.revealed .pillar:nth-child(2) .drawPath {
  transition-delay: 450ms;
}
.revealed .pillar:nth-child(3) .drawPath {
  transition-delay: 700ms;
}

.revealed .drawPath {
  stroke-dashoffset: 0;
}

/* Divisor del ícono de "sin intermediación" — mantiene su dasharray decorativo
   (2 3) y aparece con un fade ligado al pilar, no con draw animation. */
.divider {
  opacity: 0;
  transition: opacity 600ms var(--ease-decelerate);
}

.revealed .pillar:nth-child(3) .divider {
  opacity: 1;
  transition-delay: 950ms;
}

@media (prefers-reduced-motion: reduce) {
  .pillars,
  .drawPath,
  .divider {
    opacity: 1;
    transition: none;
  }
  .drawPath {
    stroke-dashoffset: 0;
  }
}

/* ─── Texto ─── */

.pillarTitle {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-xl);
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: var(--color-text);
  margin: var(--space-1) 0 0;
  transition: color var(--duration-base) var(--ease-standard);
}

/* Único hover de la sección: el título toma el color de marca.
   Sin transform, sin scale — sobriedad. */
.pillar:hover .pillarTitle,
.pillar:focus-within .pillarTitle {
  color: var(--blue-700);
}

.pillarBody {
  font-family: var(--font-body);
  font-size: var(--text-base);
  line-height: 1.6;
  color: var(--color-text-muted);
  margin: 0;
  max-width: 32ch;
}
